import React from 'react';
import { InstagramIcon, MailIcon, MessageCircleIcon, UsersIcon } from 'lucide-react';
import { Container, Section } from '../components/ui/Section';
import { PageHeader } from '../components/ui/PageHeader';
import { ButtonExternal } from '../components/ui/Button';
import { NewTabHint } from '../components/ui/NewTabHint';
import { DoodleStar } from '../components/brand/Doodles';
import { emailAddress, externalLinks } from '../data/site';
const contactCards = [{
  icon: MailIcon,
  label: 'Email',
  value: emailAddress,
  href: externalLinks.email
}, {
  icon: InstagramIcon,
  label: 'Instagram',
  value: '@ucfbcm',
  href: externalLinks.instagram
}, {
  icon: UsersIcon,
  label: 'Discord',
  value: 'Join the UCF BCM Discord',
  href: externalLinks.discord
}, {
  icon: MessageCircleIcon,
  label: 'Official UCF Organization Page',
  value: 'View UCF BCM on KnightConnect',
  href: externalLinks.knightConnect
}];
const visitingDetails = [{
  term: 'When',
  detail: 'Thursdays at 7:30 PM during the school year'
}, {
  term: 'Usual location',
  detail: 'Cape Florida Ballroom, Room 316, UCF Student Union third floor'
}, {
  term: 'Reminder',
  detail: 'Check the Events page or Instagram for the latest location.'
}];
export function Connect() {
  return <>
      <PageHeader eyebrow="Connect" title="Let’s get connected" intro="Interested in a community group, planning your first Thursday, or simply have a question? Share your information and a BCM leader can follow up." data-id="element-451" data-mp="2fcfv2teqkkuc" />

      <Section className="relative overflow-hidden bg-[#fffdf6] py-14 sm:py-18" labelledBy="form-heading" data-id="element-452" data-mp="2tbypykijdzop">
        <DoodleStar className="pointer-events-none absolute right-8 top-8 h-10 w-10" color="var(--bcm-yellow)" data-id="element-453" data-mp="2ya8mayfzz6bw" />
        <Container className="grid items-start gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-12" data-id="element-454" data-mp="3m02wf88xmgsg">
          <div className="rounded-[24px] border-2 border-brand-navy bg-white p-6 sm:p-8" data-id="element-455" data-mp="3qrcn8c67vq5x">
            <h2 id="form-heading" className="font-serif text-2xl font-semibold leading-snug text-brand-navy sm:text-3xl" data-id="element-456" data-mp="4i76pnav9sve">
              Tell us how we can help
            </h2>
            <p className="mt-3 font-sans text-[16px] leading-relaxed text-ink" data-id="element-457" data-mp="3qnucglkm7qmp">
              The Get Connected form goes straight to the BCM leadership team. If the embedded form
              below does not load in your browser, use the button to open it in a new tab.
            </p>
            <ButtonExternal href={externalLinks.getConnectedForm} className="mt-6" data-id="element-458" data-mp="3cx7ckq34bup1">
              Open the Get Connected Form
            </ButtonExternal>

            <div className="mt-7 overflow-hidden rounded-[20px] border-2 border-brand-navy bg-brand-tan" data-id="element-459" data-mp="37wbbsle2conu">
              <iframe src={`${externalLinks.getConnectedForm}?embedded=true`} title="UCF BCM Get Connected form" loading="lazy" className="h-[520px] w-full border-0 bg-white" data-id="element-460" data-mp="32xgi2oek5foy">
                Loading the Get Connected form…
              </iframe>
            </div>
            <p className="mt-3 font-meta text-[12.5px] text-ink" data-id="element-461" data-mp="1eevqzdlpmp5r">
              The form is hosted by Google. Nothing you type is stored by this website.
            </p>
          </div>

          <div data-id="element-462" data-mp="1ngvb4es0u9cx">
            <h2 className="font-serif text-2xl font-semibold text-brand-navy" data-id="element-463" data-mp="1nfaiiup9p0hp">Other ways to reach us</h2>
            <ul className="mt-5 space-y-4" data-id="element-464" data-mp="qrdx9civxh05">
              {contactCards.map(card => <li key={card.label} className="rounded-[20px] border-2 border-brand-navy bg-brand-tan p-5" data-id="element-465" data-mp="uspr5u8skoe5">
                  <div className="flex items-start gap-3" data-id="element-466" data-mp="14hhe1nyzwii7">
                    <card.icon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-brand-navy" data-id="element-467" data-mp="14j27195nvtdy" />
                    <div className="min-w-0" data-id="element-468" data-mp="71tr69u6mmvt">
                      <p className="font-meta text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-navy" data-id="element-469" data-mp="c0ojc2e1hnsl">
                        {card.label}
                      </p>
                      <a href={card.href} {...card.href.startsWith('mailto:') ? {} : {
                    target: '_blank',
                    rel: 'noopener noreferrer'
                  }} className="mt-1 inline-block break-words font-display text-base font-semibold text-brand-navy underline underline-offset-4 hover:no-underline" data-id="element-470" data-mp="29or08au2h1tq">
                        {card.value}
                        {card.href.startsWith('mailto:') ? null : <NewTabHint data-id="element-471" data-mp="36erl3yrbey5r" />}
                      </a>
                    </div>
                  </div>
                </li>)}
            </ul>

            <div className="mt-6 rounded-[20px] border-2 border-brand-navy bg-brand-blue p-5" data-id="element-472" data-mp="32e7lmoj9rigg">
              <h3 className="font-display text-xl font-bold text-brand-navy" data-id="element-473" data-mp="1c1a4kvpbv1k8">Visiting Thursday?</h3>
              <dl className="mt-3 space-y-3" data-id="element-474" data-mp="18180kfw4dw1s">
                {visitingDetails.map(item => <div key={item.term} data-id="element-475" data-mp="xw9tj4au4qwx">
                    <dt className="font-meta text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-navy" data-id="element-476" data-mp="11wtqo7veruj2">
                      {item.term}
                    </dt>
                    <dd className="mt-0.5 font-sans text-[15px] leading-relaxed text-ink" data-id="element-477" data-mp="o3uby83ga4ck">
                      {item.detail}
                    </dd>
                  </div>)}
              </dl>
              <ButtonExternal href={externalLinks.studentUnion} variant="secondary" className="mt-5 w-full sm:w-auto" data-id="element-478" data-mp="j6kgaapgwwga">
                Get Directions
              </ButtonExternal>
            </div>
          </div>
        </Container>
      </Section>
    </>;
}